import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Shield, Package, Users, DollarSign, BarChart3, MessageCircle, CreditCard } from 'lucide-react';
import logger from '../utils/logger';

const AdminDashboardPage = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      // Mock statistics for demo
      setStatistics({
        requests: {
          total: 150,
          new: 25,
          inProgress: 45,
          closed: 70,
          cancelled: 10
        },
        drivers: {
          total: 45,
          active: 38,
          blocked: 7
        },
        subscriptions: {
          total: 45,
          trial: 12,
          active: 28,
          expired: 3,
          canceled: 2,
          blocked: 5
        },
        earnings: {
          total: 125000,
          average: 1785
        }
      });
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching statistics:', error);
      toast.error('שגיאה בטעינת הסטטיסטיקות');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/');
    toast.success('התנתקת בהצלחה');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-brown-700 text-lg">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50">
      {/* Header */}
      <header id="navigation" className="header-gradient shadow-lg" tabIndex="-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Shield className="h-10 w-10 text-white" />
              <h1 className="text-3xl font-bold text-white mr-4">מערכת ניהול</h1>
            </div>
            <div className="flex items-center space-x-4 space-x-reverse">
              <span className="text-white text-lg">מנהל מערכת</span>
              <button
                onClick={handleLogout}
                className="text-white hover:text-orange-100 font-semibold transition-colors duration-300"
              >
                התנתק
              </button>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" tabIndex="-1">
        <h2 className="sr-only">סקירה כללית</h2>
        {/* Statistics Overview */}
        <div className="grid md:grid-cols-5 gap-6 mb-12">
          <div className="card text-center hover-lift">
            <div className="bg-orange-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-2xl font-bold text-brown-900">{statistics.requests.total}</h3>
            <p className="text-brown-700">סה"כ בקשות</p>
          </div>
          
          <div className="card text-center hover-lift">
            <div className="bg-orange-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-2xl font-bold text-brown-900">{statistics.drivers.total}</h3>
            <p className="text-brown-700">סה"כ מובילים</p>
          </div>
          
          <div className="card text-center hover-lift">
            <div className="bg-blue-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-brown-900">{statistics.subscriptions.active}</h3>
            <p className="text-brown-700">מנויים פעילים</p>
          </div>
          
          <div className="card text-center hover-lift">
            <div className="bg-orange-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <DollarSign className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-2xl font-bold text-brown-900">₪{statistics.earnings.total.toLocaleString()}</h3>
            <p className="text-brown-700">סה"כ הכנסות</p>
          </div>
          
          <div className="card text-center hover-lift">
            <div className="bg-orange-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-2xl font-bold text-brown-900">₪{statistics.earnings.average.toLocaleString()}</h3>
            <p className="text-brown-700">ממוצע לבקשה</p>
          </div>
        </div>

        {/* Detailed Statistics */}
        <h2 className="sr-only">סטטיסטיקות מפורטות</h2>
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Requests Status */}
          <div className="card hover-lift">
            <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-3">
                <Package className="h-5 w-5 text-orange-600" />
              </div>
              סטטוס בקשות
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <span className="text-brown-700">בקשות חדשות</span>
                <span className="font-bold text-orange-600">{statistics.requests.new}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-brown-700">בטיפול</span>
                <span className="font-bold text-blue-600">{statistics.requests.inProgress}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-success-50 rounded-lg">
                <span className="text-brown-700">הושלמו</span>
                <span className="font-bold text-success-600">{statistics.requests.closed}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-error-50 rounded-lg">
                <span className="text-brown-700">בוטלו</span>
                <span className="font-bold text-error-600">{statistics.requests.cancelled}</span>
              </div>
            </div>
          </div>

          {/* Drivers Status */}
          <div className="card hover-lift">
            <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-3">
                <Users className="h-5 w-5 text-orange-600" />
              </div>
              סטטוס מובילים
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-success-50 rounded-lg">
                <span className="text-brown-700">פעילים</span>
                <span className="font-bold text-success-600">{statistics.drivers.active}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-error-50 rounded-lg">
                <span className="text-brown-700">חסומים</span>
                <span className="font-bold text-error-600">{statistics.drivers.blocked}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <span className="text-brown-700">סה"כ</span>
                <span className="font-bold text-orange-600">{statistics.drivers.total}</span>
              </div>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="card hover-lift">
            <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
              <div className="bg-blue-100 p-3 rounded-full ml-3">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              סטטוס מנויים
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-brown-700">בתקופת ניסיון</span>
                <span className="font-bold text-blue-600">{statistics.subscriptions.trial}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-success-50 rounded-lg">
                <span className="text-brown-700">פעילים</span>
                <span className="font-bold text-success-600">{statistics.subscriptions.active}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-error-50 rounded-lg">
                <span className="text-brown-700">פגי תוקף</span>
                <span className="font-bold text-error-600">{statistics.subscriptions.expired}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-brown-700">מבוטלים</span>
                <span className="font-bold text-gray-600">{statistics.subscriptions.canceled}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="text-brown-700">חסומים</span>
                <span className="font-bold text-red-600">{statistics.subscriptions.blocked}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card hover-lift">
          <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
            <div className="bg-orange-100 p-3 rounded-full ml-3">
              <MessageCircle className="h-5 w-5 text-orange-600" />
            </div>
            פעולות מהירות
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <button
              onClick={() => navigate('/admin/drivers')}
              className="btn-primary text-lg py-4 px-6 hover-lift"
            >
              <Users className="h-5 w-5 ml-2" />
              ניהול מובילים
            </button>
            
            <button
              onClick={() => navigate('/admin/requests')}
              className="btn-secondary text-lg py-4 px-6 hover-lift"
            >
              <Package className="h-5 w-5 ml-2" />
              ניהול בקשות
            </button>
            
            <button
              onClick={() => navigate('/admin/subscriptions')}
              className="btn-primary text-lg py-4 px-6 hover-lift"
            >
              <CreditCard className="h-5 w-5 ml-2" />
              ניהול מנויים
            </button>
            
            <button
              onClick={() => navigate('/admin/reports')}
              className="btn-secondary text-lg py-4 px-6 hover-lift"
            >
              <BarChart3 className="h-5 w-5 ml-2" />
              דוחות וסטטיסטיקות
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <h2 className="sr-only">פעילות אחרונה</h2>
        <div className="card hover-lift mt-8">
          <h3 className="text-xl font-bold text-brown-900 mb-6">פעילות אחרונה</h3>
          <div className="space-y-4">
            <div className="flex items-center p-4 bg-orange-50 rounded-lg">
              <div className="bg-orange-500 w-3 h-3 rounded-full ml-3"></div>
              <div className="flex-1">
                <p className="text-brown-900 font-medium">בקשה חדשה התקבלה</p>
                <p className="text-brown-600 text-sm">הובלה מתל אביב לרמת גן</p>
              </div>
              <span className="text-brown-500 text-sm">לפני 5 דקות</span>
            </div>
            
            <div className="flex items-center p-4 bg-success-50 rounded-lg">
              <div className="bg-success-500 w-3 h-3 rounded-full ml-3"></div>
              <div className="flex-1">
                <p className="text-brown-900 font-medium">הובלה הושלמה</p>
                <p className="text-brown-600 text-sm">בקשה #REQ240901234</p>
              </div>
              <span className="text-brown-500 text-sm">לפני שעה</span>
            </div>
            
            <div className="flex items-center p-4 bg-blue-50 rounded-lg">
              <div className="bg-blue-500 w-3 h-3 rounded-full ml-3"></div>
              <div className="flex-1">
                <p className="text-brown-900 font-medium">מוביל חדש נרשם</p>
                <p className="text-brown-600 text-sm">אחמד כהן - תל אביב</p>
              </div>
              <span className="text-brown-500 text-sm">לפני שעתיים</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboardPage;
